<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import { useNavLabels } from '../../navigation/useNavLabels'

import type { BuiltNavNode } from '@/app/navigation'
import {
  freePlanLockedRoute,
  getAccountDetailPackageId,
  isFreePlanPackage,
  useAccount,
} from '@/features/account'
import orpheusWhiteLogoUrl from '@/shared/assets/logos/orpheus-white.png'
import orpheusLogoUrl from '@/shared/assets/logos/orpheus_logo.png'
import { useI18n } from '@/shared/i18n'
import { Badge } from '@/shared/ui/badge'
import { Icon, ICON_NAMES, type IconName } from '@/shared/ui/icon'

const props = withDefaults(
  defineProps<{ item: BuiltNavNode; expanded?: boolean; depth?: number }>(),
  {
    expanded: false,
    depth: 0,
  },
)
const emit = defineEmits<{ navigate: [item: BuiltNavNode] }>()
const { labelOf } = useNavLabels()
const { t } = useI18n()
const account = useAccount()
const planLocked = computed(
  () =>
    isFreePlanPackage(getAccountDetailPackageId(account.accountDetail.value)) &&
    freePlanLockedRoute(props.item.to?.name) !== null,
)
const accessibleLabel = computed(() => {
  const label = labelOf(props.item.labelKey, props.item.label)
  if (planLocked.value) return `${label}. ${t('shell.nav.requiresPro')}`
  return props.item.opensInNewTab ? t('shell.nav.opensInNewTab', { label }) : label
})
// An action leaf (Orphex MCP) is a real button: it goes nowhere in-app, so it carries its own
// accessible name even while expanded — the visible label never says "opens in a new tab".
const isAction = computed(() => !props.item.to && typeof props.item.action === 'function')
const leafTag = computed(() => (isAction.value ? 'button' : RouterLink))
const leafAttrs = computed(() =>
  isAction.value
    ? { type: 'button', 'aria-label': accessibleLabel.value }
    : {
        to: props.item.to,
        'aria-label': props.expanded && !planLocked.value ? undefined : accessibleLabel.value,
      },
)

/** The action runs synchronously inside the click — a tab opened later is popup-blocked. */
function onLeafClick(): void {
  if (isAction.value) void props.item.action?.()
  emit('navigate', props.item)
}

function iconName(icon?: string): IconName {
  return ICON_NAMES.includes(icon as IconName) ? (icon as IconName) : 'circle-question'
}

function imageFor(item: BuiltNavNode): string {
  return item.current && item.icon === 'orpheus' ? orpheusWhiteLogoUrl : orpheusLogoUrl
}

/** Non-v3 glyphs still use local optical sizes; exact rail masks own their 17.5px em in the registry. */

const controlBaseClasses = [
  'relative flex cursor-pointer items-center gap-sm whitespace-nowrap rounded-full p-2xs no-underline transition-[width,color,background-color,box-shadow] duration-300',
  'hover:bg-surface-sunken hover:text-primary hover:shadow-circle',
  'focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-[calc(-1*var(--ui-ring-width))] focus-visible:outline-[color:var(--ui-color-ring)]',
  'motion-reduce:transition-none',
  String.raw`[&.nav-tree-item\_\_control--current_.nav-tree-item\_\_icon]:[background:var(--ui-gradient-brand)]`,
  String.raw`[&.nav-tree-item\_\_control--current_.nav-tree-item\_\_icon]:text-primary-fg`,
].join(' ')

function controlClasses(kind: 'link' | 'summary'): string[] {
  const classes = [controlBaseClasses]
  if (props.depth > 0) {
    classes.push('min-h-8 w-full rounded-md px-xs text-[0.8125rem] font-normal leading-normal')
  } else {
    classes.push('min-h-11 w-11 text-sm font-medium leading-6')
    if (props.expanded && kind === 'link') {
      classes.push('w-[13.5rem] min-[56.0625rem]:w-[var(--ui-shell-nav-expanded-row-width)]')
    }
    if (props.expanded && kind === 'summary') {
      classes.push(
        'w-[calc(100%+0.21875rem)] min-[56.0625rem]:w-[var(--ui-shell-nav-expanded-row-width)]',
      )
    }
  }
  if (props.item.current) {
    classes.push('nav-tree-item__control--current', 'text-accent')
    if (!(props.expanded && props.depth === 0)) classes.push('shadow-none')
  } else classes.push('text-[color:var(--ui-shell-nav-neutral-fg)]')
  if (props.item.current && props.expanded && props.depth === 0)
    classes.push('bg-surface-sunken shadow-circle')
  classes.push('nav-tree-item__control--expanded')
  return classes
}
</script>

<template>
  <li
    class="nav-tree-item w-[calc(100%+2px)] flex m-0 list-none px-0.25"
    :class="{ 'nav-tree-item--nested': depth > 0 }"
  >
    <div
      v-if="item.to || isAction"
      class="nav-tree-item__group group m-0 list-none px-1 p-0 w-full flex flex-col"
    >
      <component
        :is="leafTag"
        class="nav-tree-item__link"
        :class="controlClasses('link')"
        v-bind="leafAttrs"
        :title="expanded && !planLocked ? undefined : accessibleLabel"
        :data-testid="isAction ? `nav-action-${item.navKey}` : undefined"
        @click="onLeafClick"
      >
        <span
          v-if="depth === 0"
          class="nav-tree-item__icon grid h-9 w-9 flex-[0_0_2.25rem] place-items-center rounded-full bg-surface text-fg shadow-circle"
          aria-hidden="true"
        >
          <img
            v-if="item.icon === 'orpheus'"
            :src="imageFor(item)"
            class="h-5 w-5 object-contain"
            data-icon="orpheus"
            alt=""
          />
          <Icon
            v-else
            :name="iconName(item.icon)"
            :size="16"
            v-bind="item.iconWeight ? { weight: item.iconWeight } : {}"
            presentation="legacy-rail"
          />
        </span>
        <span
          v-else
          class="nav-tree-item__marker h-1.5 w-1.5 flex-[0_0_0.375rem] rounded-full bg-current"
          aria-hidden="true"
        />
        <span
          v-if="expanded"
          class="nav-tree-item__label min-w-0 overflow-hidden text-start text-ellipsis"
          >{{ labelOf(item.labelKey, item.label) }}</span
        >
        <Badge
          v-if="expanded && item.badgeKey"
          class="nav-tree-item__badge"
          tone="info"
          size="sm"
          shape="rounded"
          >{{ labelOf(item.badgeKey) }}</Badge
        >
        <Icon
          v-if="planLocked"
          :class="[
            depth > 0 ? 'right-3' : 'right-3 top-4',
            'absolute rounded-full bg-surface p-px',
          ]"
          name="lock"
          :size="11"
          aria-hidden="true"
        />
        <Icon
          v-else-if="expanded && item.opensInNewTab"
          class="nav-tree-item__new-tab ms-auto me-2 text-muted"
          name="external-link"
          weight="light"
          :size="12"
          aria-hidden="true"
        />
      </component>
    </div>
    <details
      v-else-if="item.children?.length"
      class="nav-tree-item__group group m-0 list-none px-1 p-0 w-full flex flex-col"
      :open="item.current"
    >
      <summary
        class="nav-tree-item__summary !w-full list-none"
        :class="controlClasses('summary')"
        :aria-label="expanded ? undefined : labelOf(item.labelKey, item.label)"
        :title="expanded ? undefined : labelOf(item.labelKey, item.label)"
      >
        <span
          v-if="depth === 0"
          class="nav-tree-item__icon grid h-9 w-9 flex-[0_0_2.25rem] place-items-center rounded-full bg-surface text-fg shadow-circle"
          aria-hidden="true"
        >
          <img
            v-if="item.icon === 'orpheus'"
            :src="imageFor(item)"
            class="h-5 w-5 object-contain"
            data-icon="orpheus"
            alt=""
          />
          <Icon
            v-else
            :name="iconName(item.icon)"
            :size="16"
            v-bind="item.iconWeight ? { weight: item.iconWeight } : {}"
            presentation="legacy-rail"
          />
        </span>
        <span
          v-else
          class="nav-tree-item__marker h-1.5 w-1.5 flex-[0_0_0.375rem] rounded-full bg-current"
          aria-hidden="true"
        />
        <span
          v-if="expanded"
          class="nav-tree-item__label min-w-0 overflow-hidden text-start text-ellipsis"
          >{{ labelOf(item.labelKey, item.label) }}</span
        >
        <Icon
          v-if="expanded"
          class="nav-tree-item__chevron ms-auto mr-2 transition-transform duration-200 motion-reduce:transition-none group-open:rotate-180"
          name="chevron-down"
          weight="light"
          :size="12"
          aria-hidden="true"
        />
      </summary>
      <ul
        v-if="expanded"
        class="nav-tree-item__children ms-[1.625rem] grid grid-cols-[minmax(0,1fr)] gap-2xs p-0 pt-2xs"
      >
        <NavTreeItem
          v-for="child in item.children"
          :key="child.navKey ?? child.labelKey ?? child.to?.name ?? 'nav-child'"
          :item="child"
          :expanded="expanded"
          :depth="depth + 1"
          @navigate="emit('navigate', $event)"
        />
      </ul>
    </details>
  </li>
</template>

<style scoped>
.nav-tree-item__summary::-webkit-details-marker {
  display: none;
}
.nav-tree-item__control--current.nav-tree-item__control--expanded {
  box-shadow: var(--ui-shadow-circle);
}

.nav-tree-item__link:focus-visible,
.nav-tree-item__summary:focus-visible {
  outline: var(--ui-ring-width) solid var(--ui-color-ring);
  outline-offset: calc(-1 * var(--ui-ring-width));
}
</style>
